import {BroadcastChannel} from 'broadcast-channel';
import {renewalQueryKind} from '../queries/renewalCacheUtils';
import {isArrDependentQuery} from '../queries/arrScheduleCacheUtils';

// The installed experimental broadcaster has no query filter and replaces whole
// query states. Renewal results use revision notifications instead. Keep the
// existing wire format/transport for all other queries (including older tabs).
export const canBroadcastQuery = (queryKey, queryClient) => {
    if (queryKey?.[0] === 'data-status') return false;
    // Refetch ARR consumers using each tab's own request after an edit. A
    // delayed full-state broadcast must never overwrite a post-save result.
    if (isArrDependentQuery(queryKey)) return false;
    const kind = renewalQueryKind(queryKey);
    if (!kind || kind === 'related') return true;
    if (kind === 'matching') return false;
    const account = queryKey[0].split('_')[0];
    const status = queryClient?.getQueryCache().getAll().find(query =>
        query.queryKey[0] === 'data-status' && query.queryKey[2] === account)?.state.data;
    return status?.dataStatus?.renewal_metadata?.forecast_enabled === false;
};

export function broadcastQueryClient({queryClient, broadcastChannel, options}) {
    const channel = new BroadcastChannel(broadcastChannel, {webWorkerSupport: false, ...options});
    const cache = queryClient.getQueryCache();
    let receiving = false;
    const unsubscribe = cache.subscribe(event => {
        if (receiving || !canBroadcastQuery(event.query.queryKey, queryClient)) return;
        const {queryHash, queryKey, state} = event.query;
        if (event.type === 'updated' && event.action.type === 'success') {
            channel.postMessage({type: 'updated', queryHash, queryKey, state});
        } else if (event.type === 'removed') {
            channel.postMessage({type: 'removed', queryHash, queryKey});
        }
    });
    channel.onmessage = action => {
        if (!action?.type || !canBroadcastQuery(action.queryKey, queryClient)) return;
        receiving = true;
        try {
            const query = cache.get(action.queryHash);
            if (action.type === 'updated') {
                if (query) query.setState(action.state);
                else cache.build(queryClient, {queryHash: action.queryHash, queryKey: action.queryKey}, action.state);
            } else if (action.type === 'removed' && query) cache.remove(query);
        } finally { receiving = false; }
    };
    return () => { unsubscribe(); channel.close(); };
}
